import type { AccountStanding } from "@/lib/account-standing";
import { canPay, canTopUp } from "@/lib/billing-payer";
import { isRecargasPlan } from "@/lib/credits-plan";
import { canSwitchToRecargas } from "@/lib/recargas-switch";
import { CREDITS_TOPUP_PATH } from "@/lib/credits";

export const ACCOUNT_RESOLVE_PATH = "/app/billing/resolve";
export const RESOLVE_RECARGAS_ID = "pasar-a-recargas";
export const RESOLVE_RECARGAS_PATH = `${ACCOUNT_RESOLVE_PATH}#${RESOLVE_RECARGAS_ID}`;
export const ACCOUNT_PAY_PENDING_PATH = "/app/settings/account#pagar";
export const ACCOUNT_CARD_REGISTRATION_PATH = "/app/settings/account#registrar-tarjeta";
export const PLAN_CHANGE_PARAM = "cambiar_plan";
export const INBOX_PATH = "/app/conversations";

export function planChangePath(planId: string): string {
  return `/app/settings/account?${PLAN_CHANGE_PARAM}=${encodeURIComponent(planId)}#plan`;
}

export interface CardRegistrationInput {
  brandId: string;
  awaitsAuthorization: boolean | null | undefined;
}

export function showsCardRegistration({ brandId, awaitsAuthorization }: CardRegistrationInput): boolean {
  return brandId === "flow" && awaitsAuthorization === true;
}

export type PayOptionKind = "checkout" | "credits_debt" | "top_up";

export interface PayOption {
  kind: PayOptionKind;
  amount: number | null;
  path: string;
}

export interface ResolveOptionsInput {
  standing: AccountStanding;
  planId: string | null;
  planChangeAllowed: boolean;
  cardRegistration: boolean;
  role: string | null | undefined;
  isSuperAdmin: boolean;
}

export interface ResolveOptions {
  pay: PayOption | null;
  card: boolean;
  planChange: boolean;
  recargas: boolean;
}

function positive(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}

function payOptionFor(standing: AccountStanding): PayOption | null {
  if (standing.kind !== "grace" && standing.kind !== "paused") return null;
  const amount = positive(standing.amountOwed);
  if (standing.kind === "paused" && standing.reason === "credits_exhausted") {
    return amount !== null
      ? { kind: "credits_debt", amount, path: CREDITS_TOPUP_PATH }
      : { kind: "top_up", amount: null, path: CREDITS_TOPUP_PATH };
  }
  if (standing.kind === "paused" && standing.reason === "authorization_lost") return null;
  if (standing.action === "top_up") return { kind: "top_up", amount: null, path: CREDITS_TOPUP_PATH };
  if (standing.action === "pay") return { kind: "checkout", amount, path: ACCOUNT_PAY_PENDING_PATH };
  return null;
}

export function resolveOptions(input: ResolveOptionsInput): ResolveOptions {
  const { standing, role, isSuperAdmin } = input;
  if (standing.kind === "closed") return { pay: null, card: false, planChange: false, recargas: false };
  const candidate = payOptionFor(standing);
  const pay = candidate && (candidate.kind === "checkout" ? canPay(role) : canTopUp(role, isSuperAdmin)) ? candidate : null;
  const payer = canTopUp(role, isSuperAdmin);
  const troubled = standing.kind === "grace" || standing.kind === "paused";
  return {
    pay,
    card: input.cardRegistration && canPay(role),
    planChange: troubled && payer && input.planChangeAllowed,
    recargas: (troubled || input.cardRegistration) && canSwitchToRecargas(role) && !isRecargasPlan(input.planId),
  };
}

export function hasResolveOption(options: ResolveOptions): boolean {
  return options.pay !== null || options.card || options.planChange || options.recargas;
}

export function whatsappHref(number: string | null | undefined, message: string): string | null {
  const digits = number?.replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null;
}

export function supportRequestMessage(orgName: string | null | undefined): string {
  return orgName
    ? `Hola, necesito ayuda con el pago pendiente de la cuenta ${orgName}.`
    : "Hola, necesito ayuda con el pago pendiente de mi cuenta.";
}

export interface OrgPayer {
  name: string;
  email: string | null;
  role: string;
  viaAgency: boolean;
}

export function payerRecipients(payers: OrgPayer[]): string {
  return [...new Set(payers.map((payer) => payer.email).filter((email): email is string => !!email))].join(",");
}

export function payerRoleLabel(payer: OrgPayer): string {
  if (payer.viaAgency) return AGENCY_PAYER_LABEL;
  return PAYER_ROLE_LABELS[payer.role] ?? payer.role;
}

export function payersMailtoHref(payers: OrgPayer[], orgName: string | null | undefined, resolveUrl: string): string | null {
  const to = payerRecipients(payers);
  if (!to) return null;
  const account = orgName ? `la cuenta ${orgName}` : "nuestra cuenta";
  const subject = `La IA de ${orgName ?? "nuestra cuenta"} está en pausa`;
  const body = [
    "Hola,",
    "",
    `Hay un pago pendiente en ${account} de Flow y solo un administrador puede resolverlo.`,
    `Puedes hacerlo aquí: ${resolveUrl}`,
    "",
    "Gracias.",
  ].join("\n");
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts.length === 1 ? parts[0].slice(0, 2) : `${parts[0][0]}${parts[parts.length - 1][0]}`).toUpperCase();
}

export const CARD_AI_SENTENCE = "Tus agentes de IA empezarán a responder en cuanto quede autorizado.";
export const RECARGAS_DEBT_NOTE = "Si tienes saldo pendiente, se suma a tu primera recarga.";
export const NOTIFY_PAYERS_LABEL = "Avisarles por correo";
export const PAYER_ROLE_LABELS: Record<string, string> = { admin: "Administrador", supervisor: "Supervisor" };
export const AGENCY_PAYER_LABEL = "Administrador de tu agencia";
